import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 15000, // Iter 48: 15-s stability guard — slow backend fails fast with a friendly message
});

// Iter 48 — paths where an anonymous 401 is expected (visitor hits a logged-in-only endpoint)
// Silences console red + toasts for these. The business flow handles the empty result naturally.
const SILENT_401_PATHS = [
  "/auth/me", "/auth/refresh",
  "/favorites/me", "/clubvip/me", "/clubvip/contest-entries/me",
  "/community/posts", "/notifications/preferences",
  "/tickets/me", "/registrations/me",
];
function _isSilent401(url, status) {
  if (status !== 401) return false;
  return SILENT_401_PATHS.some((p) => (url || "").includes(p));
}

// Attach Authorization header fallback from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("eventom_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// Silent access-token renewal: on 401, try /auth/refresh once, then retry the original request.
let _refreshing = null;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    // Iter 48 — swallow expected 401s on anonymous reads of logged-in-only endpoints
    if (_isSilent401(url, status)) {
      return Promise.reject({ ...error, _silent: true });
    }
    // Iter 48 — surface slow-API timeouts gracefully instead of hanging
    if (error.code === "ECONNABORTED" || /timeout/i.test(error.message || "")) {
      if (typeof window !== "undefined" && !original?._slowNotified) {
        if (original) original._slowNotified = true;
        import("sonner").then(({ toast }) => {
          toast.error("Service is a bit slow — please retry.", { id: "api-slow", duration: 4000 });
        }).catch(() => {});
      }
      return Promise.reject(error);
    }
    // 403 on admin/organiser routes = stale/missing admin scope → show friendlier msg
    if (status === 403 && typeof window !== "undefined" && !original?._403Notified) {
      original._403Notified = true;
      try {
        const detail = error.response?.data?.detail || "";
        const isSessionIssue = /admin|only|forbidden|not allowed/i.test(String(detail)) || url.includes("/admin/");
        if (isSessionIssue) {
          // Lazy load sonner to avoid cycles
          import("sonner").then(({ toast }) => {
            toast.error("Session expired — tap to re-login.", {
              id: "session-expired",
              duration: 6000,
              action: {
                label: "Re-login",
                onClick: () => { localStorage.removeItem("eventom_token"); window.location.href = "/login"; },
              },
            });
          }).catch(() => {});
        }
      } catch { /* silent */ }
    }
    if (
      status === 401 &&
      original &&
      !original._retried &&
      !url.includes("/auth/login") &&
      !url.includes("/auth/refresh") &&
      !url.includes("/auth/me")
    ) {
      original._retried = true;
      try {
        _refreshing = _refreshing || api.post("/auth/refresh");
        const { data } = await _refreshing;
        _refreshing = null;
        if (data?.token) localStorage.setItem("eventom_token", data.token);
        return api(original);
      } catch (e) {
        _refreshing = null;
        // Only drop the local session when the server explicitly rejected the refresh token.
        if (e?.response?.status === 401) localStorage.removeItem("eventom_token");
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
